import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  Container,
  Paper,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";

import "./colors.css";

// Maps "Dark Blue" → CSS class from colors.css
const colourTeamToClass = {
  "Dark Blue": "dblue",
  "Light Blue": "lblue",
  "Dark Red": "dred",
  "Light Red": "lred",
  "Dark Orange": "dorange",
  "Light Orange": "lorange",
  "Dark Yellow": "dyellow",
  "Light Yellow": "lyellow",
  "Dark Green": "dgreen",
  "Light Green": "lgreen",
  "Dark Teal": "dteal",
  "Light Teal": "lteal",
  "Dark Purple": "dpurple",
  "Light Purple": "lpurple",
  "Dark Pink": "dpink",
  "Light Pink": "lpink",
  "Dark Brown": "dbrown",
  "Light Brown": "lbrown",
};

const ColourLookup = () => {
  const [watiam, setWatiam] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setWatiam(e.target.value);
    setNotFound(false);
    setResult(null);
  };

  const handleLookup = async () => {
    const trimmed = watiam.trim();
    if (!trimmed) return;

    setLoading(true);
    setResult(null);
    setNotFound(false);

    try {
      const response = await fetch("/tables/allocated_first_years.no-keep.csv");
      if (!response.ok) throw new Error("Could not load data");
      const text = await response.text();

      // Skip header row, parse each line
      const lines = text.trim().split("\n").slice(1);
      const match = lines
        .map((line) => {
          // Split on commas, but only the first 3 — Colour Team may have spaces but no commas
          const parts = line.split(",");
          return {
            fullname: parts[0]?.trim(),
            department: parts[1]?.trim(),
            watiam: parts[2]?.trim(),
            colourTeam: parts[3]?.trim(),
          };
        })
        .find((row) => row.watiam?.toLowerCase() === trimmed.toLowerCase());

      if (match) {
        setResult(match);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const colourClass = result ? colourTeamToClass[result.colourTeam] : "";

  return (
    <Box>
      {/* Hero banner */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: "#5D0096",
          color: "white",
          borderRadius: 0,
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <GroupsIcon sx={{ fontSize: "3rem", opacity: 0.9 }} />
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "2.2rem", md: "3rem" },
                textShadow: "2px 2px 4px rgba(0,0,0,0.25)",
              }}
            >
              Find Your Colour Team
            </Typography>
            <Typography
              variant="h5"
              sx={{ maxWidth: 700, lineHeight: 1.9, opacity: 0.95 }}
            >
              Enter your WatIAM username below to find out which Colour Team
              you've been assigned to for Orientation Week.
            </Typography>
          </Stack>
        </Container>
      </Paper>

      {/* Lookup form */}
      <Container maxWidth="sm" sx={{ pb: 6 }}>
        <Stack spacing={3} alignItems="center">
          {/* Search bar */}
          <Paper
            elevation={2}
            sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 4, width: "100%" }}
          >
            <Stack spacing={2}>
              <Typography variant="h5" align="center" fontWeight={600}>
                WatIAM Lookup
              </Typography>
              <Divider />
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  label="WatIAM Username"
                  variant="outlined"
                  value={watiam}
                  onChange={(e) => handleChange(e)}
                  onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                  placeholder="e.g. j3smith"
                  size="medium"
                  inputProps={{ autoCapitalize: "none", autoCorrect: "off" }}
                />
                <Button
                  variant="contained"
                  color="engPrimary"
                  onClick={handleLookup}
                  disabled={loading || !watiam.trim()}
                  endIcon={<SearchIcon />}
                  sx={{
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Look Up
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Not found message */}
          {notFound && (
            <Alert severity="warning" sx={{ width: "100%", borderRadius: 3 }}>
              No record found for <strong>{watiam.trim()}</strong>. Double-check
              your WatIAM username and try again.
            </Alert>
          )}

          {/* Result card */}
          {result && (
            <Card
              elevation={3}
              sx={{ width: "100%", borderRadius: 4, overflow: "hidden" }}
            >
              {/* Colour team banner */}
              <Box
                className={colourClass}
                sx={{
                  py: 3,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.8rem", md: "2.2rem" },
                  }}
                >
                  {result.colourTeam}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.85, mt: 0.5 }}>
                  Your Orientation Colour Team
                </Typography>
              </Box>

              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {result.fullname}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      WatIAM
                    </Typography>
                    <Chip
                      label={result.watiam}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <Divider />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1">{result.department}</Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ColourLookup;
